import { useState, useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { usePagination } from "./usePagination";
import { useAuthenticatedApi } from "./useAuthenticatedApi";
import { SpecialProductItem } from "../data/specialProductsData";

interface UseSpecialProductsOptions {
  hotelId: string;
  initialPage?: number;
  initialLimit?: number;
  refreshInterval?: number;
}

export const useSpecialProducts = ({
  hotelId,
  initialPage = 1,
  initialLimit = 20,
  refreshInterval = 30000,
}: UseSpecialProductsOptions) => {
  const { callApi } = useAuthenticatedApi();
  const queryClient = useQueryClient();

  const [items, setItems] = useState<SpecialProductItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [selectedItem, setSelectedItem] = useState<SpecialProductItem | null>(
    null
  );
  const [cache, setCache] = useState<Record<string, SpecialProductItem>>({});
  const [calculatedHasMore, setCalculatedHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const { pagination, setPage, setLimit, setTotal } = usePagination({
    initialPage,
    initialLimit,
  });

  // Cache management
  const updateCache = useCallback(
    (item: SpecialProductItem | null | undefined) => {
      if (!item || !item.productId) {
        console.warn("Cannot update cache with invalid item:", item);
        return;
      }
      setCache((prev) => ({
        ...prev,
        [item.productId]: item,
      }));
    },
    []
  );

  const removeFromCache = useCallback((id: string) => {
    setCache((prev) => {
      const newCache = { ...prev };
      delete newCache[id];
      return newCache;
    });
  }, []);

  const {
    data: productsResponse,
    isLoading: queryLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: [
      "special-products",
      hotelId,
      pagination.page,
      pagination.limit,
      searchTerm.trim() || "",
    ],
    enabled: !!hotelId,
    refetchInterval:
      refreshInterval && refreshInterval > 0 ? refreshInterval : false,
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append("page", pagination.page.toString());
      params.append("limit", pagination.limit.toString());
      params.append("isActive", "true");
      if (searchTerm.trim()) {
        params.append("name", searchTerm.trim());
      }

      const response = await callApi({
        method: "GET",
        url: `/management/special-products?${params.toString()}`,
      });

      const fetchedItems = response.data.data || [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const mappedItems: SpecialProductItem[] = fetchedItems.map((item: any) => ({
        ...item,
        amount: parseFloat(item.price?.amount ?? item.priceAmount ?? 0),
        imageUrl: item.imageUrl || item.imageurl || item.image_file || "",
      }));

      const total =
        typeof response.data.meta?.totalCount === "number"
          ? response.data.meta.totalCount
          : mappedItems.length;

      return {
        items: mappedItems,
        total,
        calculatedHasMore: mappedItems.length === pagination.limit,
      };
    },
  });

  // Sync query state into existing local state + pagination
  useEffect(() => {
    setIsLoading(queryLoading);
  }, [queryLoading]);

  useEffect(() => {
    setError(queryError instanceof Error ? queryError : null);
  }, [queryError]);

  useEffect(() => {
    if (!productsResponse) return;
    const { items: mappedItems, total, calculatedHasMore } = productsResponse;

    setItems(mappedItems);
    setTotal(total);
    setCalculatedHasMore(calculatedHasMore);
    mappedItems.forEach((item: SpecialProductItem) => updateCache(item));
  }, [productsResponse, setTotal, updateCache]);

  const fetchItems = useCallback(async () => {
    await refetch();
  }, [refetch]);

  // Create item
  const createItem = useCallback(
    async (
      item: Omit<SpecialProductItem, "productId" | "createdAt" | "updatedAt">
    ) => {
      try {
        const response = await callApi({
          method: "POST",
          url: "/management/special-products",
          data: {
            name: item.name,
            description: item.description,
            amount: item.amount,
            imageUrl: item.imageUrl,
          },
        });

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const rawItem: any = response.data.data;

        // Map the response to match expected structure
        const createdItem: SpecialProductItem = {
          ...rawItem,
          amount: parseFloat(
            rawItem.price?.amount ?? rawItem.priceAmount ?? rawItem.amount ?? 0
          ),
          imageUrl:
            rawItem.imageUrl || rawItem.imageurl || rawItem.image_file || "",
        };

        setItems((prev) => [...prev, createdItem]);
        updateCache(createdItem);
        queryClient.invalidateQueries({ queryKey: ["special-products", hotelId] });
        return createdItem;
      } catch (error) {
        console.error("useSpecialProducts: Error creating item:", error);
        throw error;
      }
    },
    [callApi, updateCache, queryClient, hotelId]
  );

  // Update item
  const updateItem = useCallback(
    async (id: string, updates: Partial<SpecialProductItem>) => {
      try {
        const originalItem = cache[id];
        if (!originalItem)
          throw new Error("Item not found in cache for update.");

        const response = await callApi({
          method: "PUT",
          url: `/management/special-products/${id}`,
          data: {
            name: updates.name,
            description: updates.description,
            amount: updates.amount,
            imageUrl: updates.imageUrl,
          },
        });

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const rawItem: any = response.data.data;

        // Map the response to match expected structure
        const result: SpecialProductItem = {
          ...rawItem,
          amount: parseFloat(
            rawItem.price?.amount ?? rawItem.priceAmount ?? rawItem.amount ?? 0
          ),
          imageUrl:
            rawItem.imageUrl || rawItem.imageurl || rawItem.image_file || "",
        };

        setItems((prev) =>
          prev.map((item) => (item.productId === id ? result : item))
        );
        updateCache(result);
        queryClient.invalidateQueries({ queryKey: ["special-products", hotelId] });
        return result;
      } catch (error) {
        console.error("useSpecialProducts: Error updating item:", error);
        throw error;
      }
    },
    [cache, callApi, updateCache, queryClient, hotelId]
  );

  // Delete item
  const deleteItem = useCallback(
    async (id: string) => {
      try {
        await callApi({
          method: "DELETE",
          url: `/management/special-products/${id}`,
        });

        setItems((prev) => prev.filter((item) => item.productId !== id));
        removeFromCache(id);
        queryClient.invalidateQueries({ queryKey: ["special-products", hotelId] });
      } catch (error) {
        console.error("useSpecialProducts: Error deleting item:", error);
        throw error;
      }
    },
    [callApi, removeFromCache, queryClient, hotelId]
  );

  return {
    items,
    isLoading,
    error,
    selectedItem,
    setSelectedItem,
    createItem,
    updateItem,
    deleteItem,
    pagination,
    setPage,
    setLimit,
    refresh: fetchItems,
    calculatedHasMore,
    searchTerm,
    setSearchTerm,
  };
};
